import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface QualityMetric {
  id: string
  category: string
  score: number
  target: number
  trend: string
  defect_rate: number
  inspections: number
  pass_rate: number
}

interface Defect {
  id: string
  title: string
  category: string
  severity: string
  status: string
  reported: string
  assigned_to: string
  resolution_days: number | null
}

interface Statistics {
  overall_quality_score: number
  total_inspections: number
  defects_open: number
  defects_resolved: number
  avg_resolution_time: number
  compliance_rate: number
}

export default function QualityManagementPage() {
  const [metrics, setMetrics] = useState<QualityMetric[]>([])
  const [defects, setDefects] = useState<Defect[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [metricsRes, defectsRes, statsRes] = await Promise.all([
        fetch('/api/qualitymanagement/metrics'),
        fetch('/api/qualitymanagement/defects'),
        fetch('/api/qualitymanagement/statistics')
      ])
      if (metricsRes.ok) setMetrics(await metricsRes.json())
      if (defectsRes.ok) setDefects(await defectsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching quality management data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getSeverityBadge = (severity: string) => {
    if (severity === 'high') return 'bg-red-100 text-red-800'
    if (severity === 'medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'resolved') return 'bg-green-100 text-green-800'
    if (status === 'in_progress') return 'bg-blue-100 text-blue-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Quality Management</h1>
          <p className="mt-2 text-gray-600">Phase 252 - Quality assurance and defect tracking</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Quality Score</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.overall_quality_score}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Inspections</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.total_inspections)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Open Defects</p>
              <p className="text-2xl font-bold text-red-600">{statistics.defects_open}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Resolved</p>
              <p className="text-2xl font-bold text-green-600">{statistics.defects_resolved}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Resolution</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_resolution_time}d</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Compliance</p>
              <p className="text-2xl font-bold text-green-600">{statistics.compliance_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Quality Metrics</h2>
          </div>
          <div className="p-6 space-y-4">
            {metrics.map((metric) => (
              <div key={metric.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{metric.category}</span>
                  <span className={`text-lg font-bold ${metric.score >= metric.target ? 'text-green-600' : 'text-yellow-600'}`}>{metric.score}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-3">
                  <div className={`h-2 rounded-full ${metric.score >= metric.target ? 'bg-green-500' : 'bg-yellow-500'}`} style={{width: `${metric.score}%`}}></div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Target</p>
                    <p className="font-medium">{metric.target}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Defect Rate</p>
                    <p className="font-medium text-red-600">{metric.defect_rate}%</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Inspections</p>
                    <p className="font-medium text-blue-600">{formatNumber(metric.inspections)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Pass Rate</p>
                    <p className="font-medium text-green-600">{metric.pass_rate}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Defect Tracking</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {defects.map((defect) => (
                  <tr key={defect.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{defect.title}</td>
                    <td className="px-6 py-4 text-gray-500">{defect.category}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getSeverityBadge(defect.severity)}`}>{defect.severity}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(defect.status)}`}>{defect.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{defect.assigned_to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
